<template>
    <MDBCol cols="12" md="4">
        <MDBRow class="mb-3">
            <MDBCol col="12" md="6" class="fw-bold text-uppercase">
                {{ $t("account.profile.supplySource") }}
            </MDBCol>
            <MDBCol col="12" md="6" class="fw-bold text-uppercase">
                {{ $t("account.profile.clientNrSingular") }}
            </MDBCol>
        </MDBRow>
        <MDBRow v-for="(data, index) in wholesalersDealers" :key="data.id ?? index" class="justify-content-end">
            <MDBCol col="12" md="6" v-if="data.expected_name">
                <input
                    v-if="isWholesalerSelectLocked(data, canAddUpdateCustomerNumbers())"
                    type="text"
                    class="form-control"
                    :value="labelForDealerRow(data)"
                    disabled
                    readonly
                />
                <select
                    v-else
                    class="form-select"
                    aria-label=".form-select-lg example"
                    v-model="data.wholesaler_id"
                    :key="`wholesaler-expected-${index}-${wholesalerOptionsKey}`"
                    @change="clearFieldErrors(getRowKey(data, index))"
                >
                    <option
                        v-for="wholesaler in optionsForDealerRow(data)"
                        :key="String(wholesaler.id)"
                        :value="wholesaler.id"
                    >
                        {{ wholesaler.name }}
                    </option>
                </select>
            </MDBCol>
            <MDBCol col="12" md="6" v-else>
                <input
                    v-if="isWholesalerSelectLocked(data, canAddUpdateCustomerNumbers())"
                    type="text"
                    class="form-control mb-1"
                    :value="labelForDealerRow(data)"
                    disabled
                    readonly
                />
                <select
                    v-else
                    class="form-select mb-1"
                    aria-label=".form-select-lg example"
                    v-model="data.wholesaler_id"
                    :key="`wholesaler-${index}-${wholesalerOptionsKey}`"
                    @change="clearFieldErrors(getRowKey(data, index))"
                >
                    <option
                        v-for="wholesaler in optionsForDealerRow(data)"
                        :key="String(wholesaler.id)"
                        :value="wholesaler.id"
                    >
                        {{ wholesaler.name }}
                    </option>
                </select>
            </MDBCol>
            <MDBCol col="12" md="6" :class="data?.parent_id !== null ? 'ps-2' : ''">
                <input
                    v-model="data.customer_number"
                    type="text"
                    class="form-control"
                    :placeholder="$t('account.profile.clientNrSingular')"
                    :aria-label="$t('account.profile.clientNrSingular')"
                    aria-describedby="basic-addon1"
                    :disabled="!canAddUpdateCustomerNumbers() || (data.verifiedCustomerNumber && !!data.customer_number)"
                    @input="clearFieldErrors(getRowKey(data, index))"
                />
                <div class="text-danger text-small pt-1" v-if="existingLengthErrors[getRowKey(data, index)]">
                    {{ existingLengthErrors[getRowKey(data, index)] }}
                </div>
                <div class="text-danger text-small pt-1" v-if="existingExistsErrors[getRowKey(data, index)]">
                    {{ $t('toast.customerNumberExist') }}
                </div>
            </MDBCol>
        </MDBRow>
        <NewCustomerNumber v-if="canAddUpdateCustomerNumbers() && !isCarDealer" ref="csNumberData" />
    </MDBCol>

    <MDBModal v-model="modal" tabindex="-1" centered size="xl" @hide="hideModal">
        <ConfirmCsNumber :data="csNumberData" @close="(e) => hideModal()" />
    </MDBModal>
</template>

<script setup>
import { MDBRow, MDBCol, MDBModal } from "mdb-vue-ui-kit";

import { useStore } from "vuex";
import { computed, defineExpose, watch, ref } from "vue";
import NewCustomerNumber from "@/components/User/ProfileForms/NewCustomerNumber.vue";
import ConfirmCsNumber from "@/components/User/ProfileForms/ConfirmCsNumber.vue";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { userCanAddUpdateCustomerNumbers } from "@/composables/useProfileFieldCheck";
import { useWholesalerSelect } from "@/composables/useWholesalerSelect";
import { normalizeWholesalersDealersList } from "@/utils/normalizeWholesalersDealers";

const store = useStore();
const {
  optionsForDealerRow,
  labelForDealerRow,
  isWholesalerSelectLocked,
  allWholesalers,
  wholesalers,
} = useWholesalerSelect();
const wholesalerOptionsKey = computed(
  () => `${allWholesalers.value.length}-${wholesalers.value.length}`,
);
const { tenantConfig } = useTenantConfig();
const { canAddUpdateCustomerNumbers } =
    userCanAddUpdateCustomerNumbers(tenantConfig);
const me = computed(() => store.getters.getMe);

const wholesalersDealers = computed(() =>
  normalizeWholesalersDealersList(me.value?.wholesalers_dealers),
);

const profile = computed(() => store.getters.getProfile);

const isCarDealer = ref(false);

const modal = ref(false);
const csNumberData = ref(null);
const existingExistsErrors = ref({});
const existingLengthErrors = ref({});

const getRowKey = (data, index) => data.id ?? `index-${index}`;

const clearExistingExistsError = (key) => {
    if (!existingExistsErrors.value[key]) {
        return;
    }

    const next = { ...existingExistsErrors.value };
    delete next[key];
    existingExistsErrors.value = next;
};

const clearExistingLengthError = (key) => {
    if (!existingLengthErrors.value[key]) {
        return;
    }

    const next = { ...existingLengthErrors.value };
    delete next[key];
    existingLengthErrors.value = next;
};

const clearFieldErrors = (key) => {
    clearExistingExistsError(key);
    clearExistingLengthError(key);
};

const setExistingCustomerNumberExistsError = (key) => {
    existingExistsErrors.value = { ...existingExistsErrors.value, [key]: true };
};

const setExistingCustomerNumberLengthError = (key, message) => {
    existingLengthErrors.value = { ...existingLengthErrors.value, [key]: message };
};

const setNewCustomerNumberExistsError = (index) => {
    csNumberData.value?.setExistsError?.(index);
};

const setNewCustomerNumberLengthError = (index, message) => {
    csNumberData.value?.setLengthError?.(index, message);
};

const clearCustomerNumberExistsErrors = () => {
    existingExistsErrors.value = {};
    existingLengthErrors.value = {};
    csNumberData.value?.clearExistsErrors?.();
    csNumberData.value?.clearLengthErrors?.();
};

watch(profile, async (newVal) => {
    if (
        newVal &&
        profile.value.group &&
        profile.value.group.parent?.gka === "CarDealer"
    ) {
        isCarDealer.value = true;
    }
});

const hideModal = () => {
    modal.value = false
}

const getNewCustomerNumbers = () => {
    const getter = csNumberData.value?.getCustomerNumbers;
    if (typeof getter === "function") {
        return getter();
    }

    const numbers = csNumberData.value?.customerNumbers;
    if (Array.isArray(numbers)) {
        return numbers;
    }
    if (Array.isArray(numbers?.value)) {
        return numbers.value;
    }
    return [];
};

const clearNewCustomerNumbers = () => {
    csNumberData.value?.resetCustomerNumbers?.();
};

defineExpose({
    getNewCustomerNumbers,
    clearNewCustomerNumbers,
    setExistingCustomerNumberExistsError,
    setExistingCustomerNumberLengthError,
    setNewCustomerNumberExistsError,
    setNewCustomerNumberLengthError,
    clearCustomerNumberExistsErrors,
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

#client-nr {
    padding: 9px 18px 9px 9px;
    background: $secondaryGray;

    h5 {
        margin-bottom: 30px;
    }
}
</style>
